import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Spinner, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';


import { ContabilidadContext } from '../../common/ContabilidadContext';
import { ContabilidadLayout } from '../../common/ContabilidadLayout';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import CuentaBancariaCard from './CuentaBancariaCard';
import { CuentasBancariasKPIs } from './CuentasBancariasKPIs';
import ModalEliminarCuenta from './ModalEliminarCuenta';

const CuentasBancariasContent = () => {
    const navigate = useNavigate();
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const { selectedCompany } = useContext(ContabilidadContext);

    const [cuentas, setCuentas] = useState([]);
    const [cargando, setCargando] = useState(false);
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [cuentaToDelete, setCuentaToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [bancoFiltro, setBancoFiltro] = useState('');
    const [busqueda, setBusqueda] = useState('');

    const idDatoFiscalSeleccionado = selectedCompany?.idDatoFiscal || null;

    useEffect(() => {
        // Verificar permisos o similar si es necesario
    }, []);

    useEffect(() => {
        if (idDatoFiscalSeleccionado) {
            cargarCuentas();
        } else {
            setCuentas([]);
        }
    }, [idDatoFiscalSeleccionado]);

    const cargarCuentas = async () => {
        setCargando(true);
        try {
            const response = await fetch('/api/contabilidad/bancos/CuentasBancarias/ObtenerCuentas', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idDatoFiscal: idDatoFiscalSeleccionado })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    setCuentas(JSON.parse(data.detalles));
                } else {
                    showErrorToast(data.vMessage || 'Error al cargar las cuentas bancarias');
                }
            } else {
                showErrorToast('Error de red al cargar cuentas.');
            }
        } catch (error) {
            console.error(error);
            showErrorToast('Ocurrió un error al cargar los datos.');
        } finally {
            setCargando(false);
        }
    };

    const handleNuevaCuenta = () => {
        navigate('/contabilidad/bancos/cuentas-bancarias/agregar');
    };

    const handleEditar = (cuenta) => {
        navigate(`/contabilidad/bancos/cuentas-bancarias/editar/${cuenta.idCatCuentaBancaria}`, { state: { cuenta } });
    };

    const handleEliminar = (cuenta) => {
        setCuentaToDelete(cuenta);
        setShowDeleteModal(true);
    };

    const confirmarEliminacion = async () => {
        if (!cuentaToDelete) return;
        setIsDeleting(true);

        try {
            const response = await fetch('/api/contabilidad/bancos/CuentasBancarias/EliminarCuenta', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    idCatCuentaBancaria: cuentaToDelete.idCatCuentaBancaria,
                    idDatoFiscal: idDatoFiscalSeleccionado 
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast('Cuenta eliminada correctamente.');
                    setShowDeleteModal(false);
                    cargarCuentas();
                } else {
                    showErrorToast(data.vMessage || 'No se pudo eliminar la cuenta.');
                }
            }
        } catch (error) {
            console.error(error);
            showErrorToast('Error al procesar la eliminación.');
        } finally {
            setIsDeleting(false);
        }
    };

    const cuentasFiltradas = cuentas.filter(c => {
        const coincideBanco = bancoFiltro ? c.vNombreBanco === bancoFiltro : true;
        const termino = busqueda.toLowerCase().trim();
        const coincideBusqueda = termino ? 
            (c.vNumCta?.toLowerCase().includes(termino) || c.vClabe?.toLowerCase().includes(termino)) 
            : true;
        
        return coincideBanco && coincideBusqueda;
    });

    return (
        <>
            <Card className="shadow-sm border-0">
                <Card.Body>
                    {!idDatoFiscalSeleccionado ? (
                        <div className="text-center py-4">
                            <h5 className="text-muted">Por favor selecciona una empresa (Dato Fiscal) en el selector superior para continuar.</h5>
                        </div>
                    ) : (
                        <div className="px-1 py-2">
                            {/* KPIs Globales */}
                            <CuentasBancariasKPIs />

                            <Row className="mb-4 align-items-end">
                                <Col xs={12} md={4} lg={4} className="mb-3 mb-md-0">
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1 fw-bold text-muted small">Filtrar por Banco</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon="building-columns" />
                                            </InputGroup.Text>
                                            <Form.Select 
                                                value={bancoFiltro} 
                                                onChange={(e) => setBancoFiltro(e.target.value)}
                                            >
                                                <option value="">Todos los Bancos</option>
                                                {[...new Set(cuentas.map(c => c.vNombreBanco))].filter(Boolean).sort().map(banco => (
                                                    <option key={banco} value={banco}>{banco}</option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4} lg={4} className="mb-3 mb-md-0">
                                    <Form.Group>
                                        <Form.Label className="text-700 mb-1 fw-bold text-muted small">Buscar cuentas</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon="search" />
                                            </InputGroup.Text>
                                            <Form.Control 
                                                type="text"
                                                placeholder="Buscar por cuenta o CLABE..."
                                                value={busqueda}
                                                onChange={(e) => setBusqueda(e.target.value)}
                                            />
                                            {busqueda && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setBusqueda('')}
                                                >
                                                    <FontAwesomeIcon icon="times" />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4} lg={4} className="d-flex justify-content-md-end mb-1">
                                    <Button variant="primary" onClick={handleNuevaCuenta}>
                                        <FontAwesomeIcon icon="plus" className="me-2" />
                                        Nueva Cuenta
                                    </Button>
                                </Col>
                            </Row>
                            
                            {cargando ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <Row>
                                    {cuentas.length === 0 ? (
                                        <Col className="text-center text-muted py-4">No hay cuentas bancarias registradas.</Col>
                                    ) : (
                                        cuentasFiltradas.length === 0 ? (
                                            <Col className="text-center text-muted py-4">No se encontraron cuentas que coincidan con tu búsqueda.</Col>
                                        ) : (
                                            cuentasFiltradas.map(c => (
                                                <Col xs={12} sm={6} md={6} lg={4} xl={3} className="mb-3" key={c.idCatCuentaBancaria}>
                                                    <CuentaBancariaCard 
                                                        cuenta={c} 
                                                        onEditar={handleEditar} 
                                                        onEliminar={handleEliminar} 
                                                    />
                                                </Col>
                                            ))
                                        )
                                    )}
                                </Row>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <ModalEliminarCuenta
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                cuenta={cuentaToDelete}
                onConfirm={confirmarEliminacion}
                isDeleting={isDeleting}
            />
        </>
    );
};

const CuentasBancarias = () => {
    return (
        <ContabilidadLayout
            title={
                <><i className="fas fa-university me-2" />Cuentas Bancarias</>
            }
        >
            <CuentasBancariasContent />
        </ContabilidadLayout>
    );
};

export default CuentasBancarias;
